import React from 'react';
import { Task, TaskStatus } from '../types/todo';
import { TaskCategory } from './TaskCategory';
import { TaskModals } from './TaskModals';
import { Plane, Building, LayoutGrid, ListChecks, Eye, EyeOff, Luggage, CheckCircle2, Circle, Clock } from 'lucide-react';

interface TaskListProps {
  tasks: Task[];
  departureDate?: string;
  returnDate?: string;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask: (taskId: string, title: string) => void;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onUpdateNotes: (taskId: string, notes: string) => void;
  onEditTask: (task: Task) => void;
  onDeleteSubtask: (taskId: string, subtaskId: string) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
  isPreview?: boolean;
}

export function TaskList({
  tasks,
  departureDate,
  returnDate,
  onUpdateStatus,
  onDeleteTask,
  onAddSubtask,
  onToggleSubtask,
  onUpdateNotes,
  onEditTask,
  onDeleteSubtask,
  onUpdatePriority,
  isPreview = false
}: TaskListProps) {
  const [editingNotes, setEditingNotes] = React.useState<string | null>(null);
  const [notesDraft, setNotesDraft] = React.useState<string>('');
  const [addingSubtaskFor, setAddingSubtaskFor] = React.useState<string | null>(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState<string | 'all'>('all');
  const [showCompleted, setShowCompleted] = React.useState(true);

  const completedTasksCount = React.useMemo(() => {
    return tasks.filter(task => task.status === 'completed').length;
  }, [tasks]);

  const visibleTasks = React.useMemo(() => {
    return showCompleted
      ? tasks
      : tasks.filter(task => task.status !== 'completed');
  }, [tasks, showCompleted]);

  const tasksByCategory = React.useMemo(() => {
    const grouped = visibleTasks.reduce((acc, task) => {
      const category = task.category || 'uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    }, {} as Record<string, Task[]>);

    const orderedCategories = ['before-departure', 'packing', 'after-arrival', 'return'];
    return orderedCategories.reduce((acc, category) => {
      if (grouped[category]) {
        acc[category] = grouped[category];
      }
      return acc;
    }, {} as Record<string, Task[]>);
  }, [visibleTasks]);

  const allTasksByCategory = React.useMemo(() => {
    const grouped = tasks.reduce((acc, task) => {
      const category = task.category || 'uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    }, {} as Record<string, Task[]>);

    return grouped;
  }, [tasks]);

  const handleSaveNotes = () => {
    if (editingNotes) {
      onUpdateNotes(editingNotes, notesDraft);
      setEditingNotes(null);
    }
  };

  const handleSaveSubtask = (e: React.FormEvent) => {
    e.preventDefault();
    if (addingSubtaskFor && newSubtaskTitle.trim()) {
      onAddSubtask(addingSubtaskFor, newSubtaskTitle);
      setAddingSubtaskFor(null);
      setNewSubtaskTitle('');
    }
  };

  const categories = [
    { id: 'all', label: 'Tout', icon: <LayoutGrid className="w-4 h-4" /> },
    { id: 'before-departure', label: 'Avant le départ', icon: <ListChecks className="w-4 h-4" /> },
    { id: 'packing', label: 'Valise', icon: <Luggage className="w-4 h-4" /> },
    { id: 'after-arrival', label: 'Jour J', icon: <Plane className="w-4 h-4" /> },
    { id: 'return', label: 'Arrivée', icon: <Building className="w-4 h-4" /> }
  ];

  const getCategoryStats = (catId: string) => {
    const catTasks = catId === 'all' ? tasks : (allTasksByCategory[catId] || []);
    const total = catTasks.length;
    const completed = catTasks.filter(t => t.status === 'completed').length;
    return { total, completed };
  };

  const filteredCategories = activeCategory === 'all'
    ? Object.entries(tasksByCategory)
    : tasksByCategory[activeCategory]
      ? [[activeCategory, tasksByCategory[activeCategory]] as [string, Task[]]]
      : [];

  return (
    <div className="space-y-4">
      <div className="sticky top-[7.5rem] z-30 sm:-mx-4 px-1 sm:px-4 pb-2 sm:pb-3 pt-1.5 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-md">
        <div className="flex gap-2 sm:gap-1.5 overflow-x-auto no-scrollbar pb-1 px-0.5">
          {categories.map((cat) => {
            const stats = getCategoryStats(cat.id);
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  flex items-center gap-1.5 sm:gap-1.5 px-3.5 py-2.5 sm:px-3 sm:py-2 rounded-xl text-[13px] sm:text-xs font-medium whitespace-nowrap
                  transition-all duration-200 flex-shrink-0 touch-manipulation active:scale-[0.97]
                  ${isActive
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                {cat.icon}
                <span>{cat.label}</span>
                {cat.id !== 'all' && (
                  <span className={`
                    text-[11px] sm:text-[10px] px-1.5 py-0.5 rounded-full min-w-[22px] sm:min-w-[20px] text-center
                    ${isActive
                      ? 'bg-white/20 dark:bg-gray-900/20'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }
                  `}>
                    {stats.completed}/{stats.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {completedTasksCount > 0 && !isPreview && (
          <div className="flex items-center justify-between mt-3 sm:mt-2.5 px-1 sm:px-0">
            <div className="flex items-center gap-4 sm:gap-3 text-[13px] sm:text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-emerald-500" />
                {tasks.filter(t => t.status === 'completed').length}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-amber-500" />
                {tasks.filter(t => t.status === 'in-progress').length}
              </span>
              <span className="flex items-center gap-1.5">
                <Circle className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400" />
                {tasks.filter(t => t.status === 'not-started').length}
              </span>
            </div>
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className="flex items-center gap-1.5 text-[13px] sm:text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 active:text-gray-900 dark:active:text-gray-100 transition-colors py-1 px-2 -mr-2 rounded-lg touch-manipulation"
            >
              {showCompleted ? (
                <>
                  <EyeOff className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                  <span>Masquer terminées</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                  <span>Afficher terminées ({completedTasksCount})</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Task Categories */}
      <div className="space-y-3 sm:space-y-6">
        {filteredCategories.map(([category, categoryTasks]) => (
          <TaskCategory
            key={category}
            category={category}
            tasks={categoryTasks as Task[]}
            allTasks={allTasksByCategory[category]}
            isPreview={isPreview}
            departureDate={departureDate}
            returnDate={returnDate}
            onUpdateStatus={onUpdateStatus}
            onDeleteTask={onDeleteTask}
            onAddSubtask={(taskId) => setAddingSubtaskFor(taskId)}
            onToggleSubtask={onToggleSubtask}
            onUpdateNotes={(taskId, notes) => {
              setEditingNotes(taskId);
              setNotesDraft(notes);
            }}
            onEditTask={onEditTask}
            onDeleteSubtask={onDeleteSubtask}
            onUpdatePriority={onUpdatePriority}
          />
        ))}
      </div>

      <TaskModals
        editingNotes={editingNotes}
        notesDraft={notesDraft}
        addingSubtaskFor={addingSubtaskFor}
        newSubtaskTitle={newSubtaskTitle}
        onCloseNotes={() => setEditingNotes(null)}
        onSaveNotes={handleSaveNotes}
        onUpdateNotesDraft={setNotesDraft}
        onCloseSubtask={() => {
          setAddingSubtaskFor(null);
          setNewSubtaskTitle('');
        }}
        onSaveSubtask={handleSaveSubtask}
        onUpdateSubtaskTitle={setNewSubtaskTitle}
      />
    </div>
  );
}
